import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence, useTransform, useScroll } from 'framer-motion';
import { 
  Zap, ArrowRight, Check, Star, Play,
  Shield, Sparkles, PenTool, Scale,
  Users, FolderOpen, MessageSquare,
  FileText, AlertTriangle, CheckCircle, Clock, Send
} from 'lucide-react';
import { Language, landingTranslations } from '../locales/landing';
import LivingStructureBackground from '../components/landing/LivingStructureBackground';
import FloatingElements from '../components/landing/FloatingElements';

interface LandingLinearProps {
  lang?: Language;
}

// ============================================
// ELEGANT BACKGROUND - Purple Mesh Gradient Orbs
// ============================================

const ElegantBackground = () => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 1 }}>
      {/* Primary orb - top right - Strong purple */}
      <motion.div
        className="absolute -top-[5%] right-[5%] w-[600px] h-[600px] rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, rgba(139, 92, 246, 0.15) 50%, transparent 70%)',
        }}
        animate={{
          x: [0, 50, 0],
          y: [0, 40, 0],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      {/* Secondary orb - bottom left - Indigo */}
      <motion.div
        className="absolute bottom-[0%] -left-[5%] w-[700px] h-[700px] rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.35) 0%, rgba(99, 102, 241, 0.12) 50%, transparent 70%)',
        }}
        animate={{
          x: [0, -40, 0],
          y: [0, -50, 0],
        }}
        transition={{
          duration: 22,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      {/* Center orb - Violet accent - most visible */}
      <motion.div
        className="absolute top-[20%] left-[30%] w-[500px] h-[500px] rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.3) 0%, rgba(168, 85, 247, 0.1) 50%, transparent 70%)',
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.7, 1, 0.7],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      {/* Extra orb - top left - pink accent */}
      <motion.div
        className="absolute top-[5%] left-[15%] w-[400px] h-[400px] rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(236, 72, 153, 0.25) 0%, transparent 60%)',
        }}
        animate={{
          x: [0, 30, 0],
          y: [0, 35, 0],
          scale: [1, 1.15, 1],
        }}
        transition={{
          duration: 14,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      {/* Extra orb - bottom right - deep violet */}
      <motion.div
        className="absolute bottom-[15%] right-[10%] w-[350px] h-[350px] rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(124, 58, 237, 0.3) 0%, transparent 60%)',
        }}
        animate={{
          x: [0, -35, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </div>
  );
};

// ============================================
// HERO FLOW ANIMATION - Minimalist Professional
// ============================================

const HeroFlowAnimation = () => {
  const [phase, setPhase] = useState(0);
  const query = "Analyser kontrakten for risikoer";
  const [displayedQuery, setDisplayedQuery] = useState('');

  const findings = [
    { type: 'warning', text: 'Uklar ansvarsfordeling i §4.2' },
    { type: 'warning', text: 'Mangler force majeure' },
    { type: 'success', text: 'Konkurranseklausul OK' },
  ];
  const [visibleFindings, setVisibleFindings] = useState(0);

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    if (phase === 0) {
      timers.push(setTimeout(() => setPhase(1), 1000));
    } else if (phase === 1) {
      let index = 0;
      setDisplayedQuery('');
      const typeInterval = setInterval(() => {
        if (index < query.length) {
          setDisplayedQuery(query.slice(0, index + 1));
          index++;
        } else {
          clearInterval(typeInterval);
          timers.push(setTimeout(() => setPhase(2), 400));
        }
      }, 35);
      return () => clearInterval(typeInterval);
    } else if (phase === 2) {
      timers.push(setTimeout(() => setPhase(3), 1500));
    } else if (phase === 3) {
      if (visibleFindings < findings.length) {
        timers.push(setTimeout(() => {
          setVisibleFindings(v => v + 1);
        }, 350));
      } else {
        timers.push(setTimeout(() => setPhase(4), 1200));
      }
    } else if (phase === 4) {
      timers.push(setTimeout(() => {
        setPhase(0);
        setDisplayedQuery('');
        setVisibleFindings(0);
      }, 2500));
    }

    return () => timers.forEach(t => clearTimeout(t));
  }, [phase, visibleFindings]);

  return (
    <div className="relative w-full max-w-md">
      {/* Subtle glow */}
      <div 
        className="absolute -inset-10 rounded-3xl opacity-40 blur-3xl pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15), transparent 70%)',
        }}
      />
      
      {/* Card */}
      <div
        className="relative rounded-2xl border border-white/[0.08] overflow-hidden"
        style={{
          background: 'rgba(12, 12, 16, 0.8)',
          backdropFilter: 'blur(20px)',
        }}
      >
        {/* Minimal header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.05]">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-violet-500" />
            <span className="text-xs text-white/50 font-medium tracking-wide">Lexy</span>
          </div>
          <span className="text-[10px] text-white/30">AI Analyse</span>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Document indicator */}
          <div className="flex items-center gap-3 py-2">
            <div className="w-8 h-10 rounded bg-white/[0.05] border border-white/[0.08] flex items-center justify-center">
              <FileText className="w-4 h-4 text-white/40" />
            </div>
            <div>
              <p className="text-sm text-white/70">Leverandøravtale.pdf</p>
              <p className="text-[11px] text-white/30">12 sider</p>
            </div>
          </div>

          {/* Input */}
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <div className="flex-1 min-h-[20px]">
              {phase === 0 && (
                <span className="text-white/25 text-sm">Spør om dokumentet...</span>
              )}
              {phase >= 1 && (
                <span className="text-white/70 text-sm">
                  {phase === 1 ? displayedQuery : query}
                  {phase === 1 && (
                    <motion.span
                      className="inline-block w-px h-4 bg-violet-400 ml-0.5 align-middle"
                      animate={{ opacity: [1, 0] }}
                      transition={{ duration: 0.4, repeat: Infinity }}
                    />
                  )}
                </span>
              )}
            </div>
            <motion.div
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              animate={{
                backgroundColor: phase === 1 ? 'rgba(139,92,246,1)' : 'rgba(255,255,255,0.05)',
              }}
            >
              <ArrowRight className={`w-3.5 h-3.5 ${phase === 1 ? 'text-white' : 'text-white/30'}`} />
            </motion.div>
          </div>

          {/* Results */}
          <AnimatePresence mode="wait">
            {phase === 2 && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="py-4"
              >
                <div className="flex items-center gap-3">
                  <motion.div
                    className="w-4 h-4 border-2 border-violet-500/30 border-t-violet-500 rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  <span className="text-sm text-white/40">Analyserer...</span>
                </div>
              </motion.div>
            )}

            {(phase === 3 || phase === 4) && (
              <motion.div
                key="results"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-2"
              >
                {findings.slice(0, visibleFindings).map((finding, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                    className="flex items-center gap-3 py-2.5 px-3 rounded-lg bg-white/[0.02]"
                  >
                    <div className={`w-1.5 h-1.5 rounded-full ${
                      finding.type === 'warning' ? 'bg-amber-400' : 'bg-emerald-400'
                    }`} />
                    <span className="text-sm text-white/60">{finding.text}</span>
                  </motion.div>
                ))}
                
                {phase === 4 && (
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="w-full mt-3 py-2.5 rounded-lg bg-violet-500/10 border border-violet-500/20 text-violet-400 text-sm font-medium"
                  >
                    Se full analyse
                  </motion.button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const LandingLinear: React.FC<LandingLinearProps> = ({ lang = 'nb' }) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const t = landingTranslations[lang];

  return (
    <div ref={containerRef} className="min-h-screen bg-[#08080a]">
      
      {/* Living Structure Background - Animated organic pattern */}
      <LivingStructureBackground />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50">
        <div className="max-w-6xl mx-auto px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-medium text-white/80">Lexy</span>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-white/30 hover:text-white/60 transition-colors">{t.nav.features}</a>
            <a href="#pricing" className="text-sm text-white/30 hover:text-white/60 transition-colors">{t.nav.pricing}</a>
          </div>
          
          <div className="flex items-center gap-6">
            <button 
              onClick={() => navigate(lang === 'en' ? '/en/lexy/login' : '/lexy/login')}
              className="text-sm text-white/30 hover:text-white/60 transition-colors"
            >
              {t.nav.login}
            </button>
            <button
              onClick={() => navigate(lang === 'en' ? '/en/lexy/login' : '/lexy/login')}
              className="text-sm text-white/50 hover:text-white/80 transition-colors"
            >
              {lang === 'en' ? 'Get started' : 'Kom i gang'} →
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        
        {/* Subtle ambient glow */}
        <div className="absolute top-[38%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] bg-violet-500/[0.05] blur-[100px] pointer-events-none" />
        
        <div className="text-center relative z-10 px-6">
          
          {/* Headline */}
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="text-[3.5rem] sm:text-[5rem] lg:text-[6rem] font-thin tracking-[-0.04em] leading-[0.9] mb-6"
          >
            <span className="text-white">{lang === 'en' ? 'Case management.' : 'Saksbehandling.'}</span>
            <br />
            <span className="text-white/30">{lang === 'en' ? 'Powered by AI.' : 'Drevet av AI.'}</span>
          </motion.h1>
          
          {/* Subheadline */}
          <motion.p 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="text-base text-white/45 max-w-md mx-auto mb-10 leading-relaxed"
          >
            {lang === 'en' 
              ? 'AI-powered platform for modern law firms.'
              : 'AI-drevet plattform for moderne advokatfirmaer.'}
          </motion.p>
          
          {/* CTAs - Elegant links */}
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center justify-center gap-6 mb-20"
          >
            {/* Kom i gang - Minimalist Primary CTA */}
            <motion.button
              onClick={() => navigate(lang === 'en' ? '/en/lexy/login' : '/lexy/login')}
              className="px-6 py-2.5 text-[15px] font-medium text-white bg-white/10 rounded-full border border-white/20 hover:bg-white/15 hover:border-white/40 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {lang === 'en' ? 'Get started' : 'Kom i gang'}
            </motion.button>
            
            {/* Se demo - Secondary CTA */}
            <motion.button
              className="group flex items-center gap-2 px-5 py-2.5 text-[15px] text-white/50 hover:text-white/80 transition-all duration-300 rounded-full hover:bg-white/5"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="relative flex items-center justify-center w-5 h-5">
                <span className="absolute inset-0 rounded-full bg-white/10 group-hover:bg-white/15 transition-colors duration-300" />
                <Play className="relative w-3 h-3 fill-current ml-0.5" />
              </span>
              {lang === 'en' ? 'Watch demo' : 'Se demo'}
            </motion.button>
          </motion.div>
          
          {/* Feature icons - With hover lift and glow */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="flex items-center justify-center gap-14"
          >
            {[
              { icon: FolderOpen, label: lang === 'en' ? 'Cases' : 'Saker' },
              { icon: FileText, label: lang === 'en' ? 'Docs' : 'Dokumenter' },
              { icon: Sparkles, label: 'AI' },
              { icon: Clock, label: lang === 'en' ? 'Time' : 'Timer' },
              { icon: Users, label: 'Team' },
            ].map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.06 }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="group relative flex flex-col items-center gap-3 cursor-pointer"
              >
                {/* Subtle glow on hover */}
                <span className="absolute -inset-4 rounded-2xl bg-violet-500/0 group-hover:bg-violet-500/10 blur-xl transition-all duration-500" />
                <div className="relative p-3 rounded-xl bg-white/0 group-hover:bg-white/5 transition-all duration-300">
                  <item.icon className="w-6 h-6 text-white/50 group-hover:text-white/90 transition-all duration-300" strokeWidth={1.5} />
                </div>
                <span className="relative text-[11px] text-white/50 group-hover:text-white/90 tracking-[0.12em] uppercase transition-all duration-300">{item.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
        >
          <motion.div
            animate={{ y: [0, 5, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-px h-10 bg-gradient-to-b from-white/25 to-transparent"
          />
        </motion.div>
      </section>

      {/* People Section */}
      <section className="relative py-24 bg-gradient-to-b from-[#08080a] to-[#0f0f12]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4 tracking-tight">
              {lang === 'en' 
                ? 'Built for lawyers who want to focus on what matters most'
                : 'Bygget for advokater som vil fokusere på det som betyr mest'}
            </h2>
            <p className="text-white/40 text-lg max-w-2xl mx-auto">
              {lang === 'en'
                ? 'We help legal teams free up time from repetitive tasks, so they can create greater value for their clients.'
                : 'Vi hjelper juridiske team med å frigjøre tid fra repetitive oppgaver, slik at de kan skape større verdi for sine klienter.'}
            </p>
          </motion.div>

          {/* Image Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { 
                img: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&h=700&fit=crop&crop=faces',
                color: 'from-violet-500/20'
              },
              { 
                img: 'https://images.unsplash.com/photo-1556157382-97edd2f9e914?w=600&h=700&fit=crop&crop=faces',
                color: 'from-amber-500/20',
                text: lang === 'en' ? 'Focus on clients' : 'Fokuser på klientene',
                subtext: lang === 'en' ? 'Let AI handle the routine' : 'La AI ta seg av det rutinepregede'
              },
              { 
                img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=700&fit=crop&crop=faces',
                color: 'from-indigo-500/20'
              },
              { 
                img: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=600&h=700&fit=crop&crop=faces',
                color: 'from-emerald-500/20'
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`relative overflow-hidden rounded-2xl ${i === 1 ? 'bg-amber-400' : ''}`}
              >
                {item.text ? (
                  <div className="aspect-[3/4] p-6 flex flex-col justify-end bg-gradient-to-br from-amber-400 to-amber-500">
                    <h3 className="text-xl md:text-2xl font-semibold text-[#08080a] mb-2">{item.text}</h3>
                    <p className="text-[#08080a]/70 text-sm">{item.subtext}</p>
                  </div>
                ) : (
                  <div className="aspect-[3/4] relative group">
                    <img 
                      src={item.img} 
                      alt="Legal professional"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${item.color} to-transparent opacity-60`} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-28">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-indigo-400 text-xs uppercase tracking-[0.3em] font-medium mb-4"
            >
              {lang === 'en' ? 'Platform' : 'Plattformen'}
            </motion.p>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-semibold text-white mb-5 tracking-tight"
            >
              {lang === 'en' ? 'Everything you need' : 'Alt du trenger'}
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-white/35 text-base max-w-lg mx-auto"
            >
              {lang === 'en' 
                ? 'A complete solution for modern legal work. Save time, reduce errors, impress clients.'
                : 'En komplett løsning for moderne juridisk arbeid. Spar tid, reduser feil, imponér klienter.'}
            </motion.p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {(lang === 'en' ? [
              { icon: FolderOpen, title: 'Case Management', desc: 'Organize cases, deadlines and teams. Never miss a deadline again.', color: '#6366f1' },
              { icon: Users, title: 'Client Portal', desc: 'Give clients access to case status, documents and secure communication.', color: '#8b5cf6' },
              { icon: MessageSquare, title: 'Communication', desc: 'Email, messages and notes — all gathered in the case it belongs to.', color: '#3b82f6' },
              { icon: Sparkles, title: 'AI Document Analysis', desc: 'Upload contracts and get instant analysis of risks and key points.', color: '#a855f7' },
              { icon: PenTool, title: 'AI Writing Assistant', desc: 'Generate drafts for briefs, contracts and letters in seconds.', color: '#06b6d4' },
              { icon: Shield, title: 'Compliance & Security', desc: 'GDPR-compliant, encrypted storage, and full audit trail.', color: '#10b981' },
            ] : [
              { icon: FolderOpen, title: 'Sakshåndtering', desc: 'Organiser saker, frister og team. Aldri gå glipp av en deadline igjen.', color: '#6366f1' },
              { icon: Users, title: 'Klientportal', desc: 'Gi klienter tilgang til saksstatus, dokumenter og sikker kommunikasjon.', color: '#8b5cf6' },
              { icon: MessageSquare, title: 'Kommunikasjon', desc: 'E-post, meldinger og notater — alt samlet i saken det tilhører.', color: '#3b82f6' },
              { icon: Sparkles, title: 'AI Dokumentanalyse', desc: 'Last opp kontrakter og få umiddelbar analyse av risikoer og nøkkelpunkter.', color: '#a855f7' },
              { icon: PenTool, title: 'AI Skriveassistent', desc: 'Generer utkast til prosesskriv, kontrakter og brev på sekunder.', color: '#06b6d4' },
              { icon: Shield, title: 'Compliance & Sikkerhet', desc: 'GDPR-compliant, kryptert lagring, og full revisjonsspor.', color: '#10b981' },
            ]).map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="group p-6 rounded-2xl border border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-300"
              >
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110 duration-300"
                  style={{ backgroundColor: `${feature.color}15` }}
                >
                  <feature.icon className="w-5 h-5" style={{ color: feature.color }} />
                </div>
                <h3 className="text-base font-medium text-white mb-2">{feature.title}</h3>
                <p className="text-white/35 text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 border-y border-white/[0.03]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {(lang === 'en' ? [
              { value: '10x', label: 'Faster document analysis' },
              { value: '50+', label: 'Law firms' },
              { value: '5000+', label: 'Cases handled' },
              { value: '47h', label: 'Saved per week' },
            ] : [
              { value: '10x', label: 'Raskere dokumentanalyse' },
              { value: '50+', label: 'Advokatfirmaer' },
              { value: '5000+', label: 'Saker håndtert' },
              { value: '47t', label: 'Spart per uke' },
            ]).map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-semibold text-white mb-2 tracking-tight">{stat.value}</div>
                <div className="text-white/25 text-xs">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-28">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex justify-center gap-0.5 mb-8">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
              ))}
            </div>
            <blockquote className="text-xl md:text-2xl text-white/80 font-normal leading-relaxed mb-10">
              {lang === 'en' 
                ? '"Lexy has transformed how we work. We save several hours every day on document review and case administration. The AI features are impressively accurate."'
                : '"Lexy har transformert hvordan vi jobber. Vi sparer flere timer hver dag på dokumentgjennomgang og saksadministrasjon. AI-funksjonene er imponerende presise."'}
            </blockquote>
            <div className="flex items-center justify-center gap-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white text-sm font-medium">
                MH
              </div>
              <div className="text-left">
                <div className="text-white/70 text-sm font-medium">Maria Hansen</div>
                <div className="text-white/25 text-xs">{lang === 'en' ? 'Partner, Strand Law' : 'Partner, Strand Advokat'}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-28">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-emerald-400 text-xs uppercase tracking-[0.3em] font-medium mb-4"
            >
              {lang === 'en' ? 'Pricing' : 'Priser'}
            </motion.p>
            <h2 className="text-4xl md:text-5xl font-semibold text-white mb-5 tracking-tight">
              {lang === 'en' ? 'Simple, transparent pricing' : 'Enkel, transparent prising'}
            </h2>
            <p className="text-white/35 text-base">
              {lang === 'en' ? '14-day free trial. No credit card required.' : '14 dagers gratis prøveperiode. Ingen kredittkort.'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl border border-white/[0.05] bg-white/[0.01] hover:border-white/[0.1] transition-colors"
            >
              <h3 className="text-lg font-medium text-white mb-1">Starter</h3>
              <p className="text-white/25 text-sm mb-6">
                {lang === 'en' ? 'For small teams and solo lawyers' : 'For mindre team og enkeltadvokater'}
              </p>
              <div className="mb-8">
                <span className="text-4xl font-semibold text-white">990</span>
                <span className="text-white/25 ml-1 text-sm">{lang === 'en' ? 'NOK/mo' : 'kr/mnd'}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {(lang === 'en' 
                  ? ['Up to 5 users', '50 active cases', 'AI analysis (100/mo)', 'Document management', 'Email support']
                  : ['Opptil 5 brukere', '50 aktive saker', 'AI-analyse (100/mnd)', 'Dokumenthåndtering', 'E-post support']
                ).map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/40 text-sm">
                    <Check className="w-4 h-4 text-white/20" />{f}
                  </li>
                ))}
              </ul>
              <button className="w-full py-3 rounded-full bg-white/[0.05] text-white/70 text-sm font-medium hover:bg-white/[0.1] transition-colors">
                {lang === 'en' ? 'Start free' : 'Start gratis'}
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="relative p-8 rounded-2xl border border-white/[0.1] bg-white/[0.02]"
            >
              <div className="absolute -top-3 left-6 px-3 py-1 bg-white text-[#08080a] rounded-full text-xs font-medium">
                {lang === 'en' ? 'Recommended' : 'Anbefalt'}
              </div>
              <h3 className="text-lg font-medium text-white mb-1">Professional</h3>
              <p className="text-white/25 text-sm mb-6">
                {lang === 'en' ? 'For growing firms with high demands' : 'For voksende firmaer med høye krav'}
              </p>
              <div className="mb-8">
                <span className="text-4xl font-semibold text-white">2 490</span>
                <span className="text-white/25 ml-1 text-sm">{lang === 'en' ? 'NOK/mo' : 'kr/mnd'}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {(lang === 'en'
                  ? ['Unlimited users', 'Unlimited cases', 'Unlimited AI', 'Client portal included', 'Priority support', 'API access']
                  : ['Ubegrenset brukere', 'Ubegrenset saker', 'Ubegrenset AI', 'Klientportal inkludert', 'Prioritert support', 'API-tilgang']
                ).map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/40 text-sm">
                    <Check className="w-4 h-4 text-emerald-400" />{f}
                  </li>
                ))}
              </ul>
              <motion.button 
                className="w-full py-3 rounded-full bg-white text-[#08080a] text-sm font-medium"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {lang === 'en' ? 'Start free trial' : 'Start gratis prøveperiode'}
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-28">
        <div className="max-w-xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-5 tracking-tight">
              {lang === 'en' ? 'Ready to save time?' : 'Klar til å spare tid?'}
            </h2>
            <p className="text-white/35 text-base mb-10">
              {lang === 'en' 
                ? 'Join 50+ law firms already using Lexy.'
                : 'Bli med 50+ advokatfirmaer som allerede bruker Lexy.'}
              <br />
              {lang === 'en' ? '14 days free. No commitment.' : '14 dager gratis. Ingen binding.'}
            </p>
            <motion.button
              onClick={() => navigate(lang === 'en' ? '/en/lexy/login' : '/lexy/login')}
              className="group px-8 py-3.5 bg-white text-[#08080a] rounded-full text-sm font-medium inline-flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {lang === 'en' ? 'Start free now' : 'Start gratis nå'}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.03] py-12">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Zap className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-white/60 text-sm font-medium">Lexy</span>
          </div>
          <div className="flex items-center gap-8 text-white/30 text-sm">
            <a href="#" className="hover:text-white/60 transition-colors">{t.footer.terms}</a>
            <a href="#" className="hover:text-white/60 transition-colors">{t.footer.privacy}</a>
            <a href="#" className="hover:text-white/60 transition-colors">{lang === 'en' ? 'Contact' : 'Kontakt'}</a>
          </div>
          <p className="text-white/20 text-sm">© 2024 Lexy</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingLinear;
